import { logger } from "@/lib/logger";
/**
 * CRASH-SAFE: Minimal offline queue for edge function calls
 * Wraps supabase.functions.invoke to queue calls when offline
 * 
 * All localStorage operations are wrapped in try-catch for safety
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  safeGetStorage, 
  safeSetStorage, 
  safeIsOnline, 
  safeDispatchEvent,
  hasWindow 
} from '@/lib/browserSafeUtils';

interface QueuedEdgeFunction {
  id: string;
  functionName: string;
  body: any;
  timestamp: number;
  retries: number; // Track retry attempts
}

const MAX_EDGE_RETRIES = 3;

const QUEUE_KEY = 'offline-edge-functions';

export async function invokeWithOfflineSupport(
  functionName: string,
  body: any
): Promise<{ data: any; error: any }> {
  try {
    // If online, call directly
    if (safeIsOnline()) {
      return supabase.functions.invoke(functionName, { body });
    }

    // If offline, queue the request
    const queuedCall: QueuedEdgeFunction = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      functionName,
      body,
      timestamp: Date.now(),
      retries: 0,
    };

    const stored = safeGetStorage(QUEUE_KEY);
    const queue = stored ? JSON.parse(stored) : [];
    queue.push(queuedCall);
    
    if (safeSetStorage(QUEUE_KEY, JSON.stringify(queue))) {
      toast.info('Offline mode', {
        description: 'Request saved. Will sync when connection restored.',
      });
      
      // Dispatch update event safely
      safeDispatchEvent('offline-queue-updated');

      return { data: { queued: true, id: queuedCall.id }, error: null };
    } else {
      return { data: null, error: { message: 'Failed to queue offline request - storage unavailable' } };
    }
  } catch (error) {
    logger.error('[offlineEdgeFunctions] invokeWithOfflineSupport error:', error);
    return { data: null, error: { message: 'Failed to process request' } };
  }
}

// Process queued requests when online
export async function processOfflineQueue() {
  try {
    if (!safeIsOnline()) return;

    const stored = safeGetStorage(QUEUE_KEY);
    if (!stored) return;

    let queue: QueuedEdgeFunction[];
    try {
      queue = JSON.parse(stored);
    } catch {
      // Invalid JSON, clear the queue
      safeSetStorage(QUEUE_KEY, '[]');
      return;
    }

    if (!Array.isArray(queue) || queue.length === 0) return;

    logger.debug(`Processing ${queue.length} queued offline requests...`);
    const failedRequests: QueuedEdgeFunction[] = [];

    for (const request of queue) {
      try {
        const { error } = await supabase.functions.invoke(request.functionName, {
          body: request.body,
        });

        if (error) {
          request.retries = (request.retries || 0) + 1;
          if (request.retries < MAX_EDGE_RETRIES) {
            failedRequests.push(request);
          } else {
            logger.error(`[offlineEdgeFunctions] Max retries (${MAX_EDGE_RETRIES}) reached for: ${request.functionName}, discarding`);
          }
        } else {
          logger.debug(`Successfully processed offline request: ${request.functionName}`);
        }
      } catch (error) {
        logger.error(`Failed to process offline request: ${request.functionName}`, error);
        request.retries = (request.retries || 0) + 1;
        if (request.retries < MAX_EDGE_RETRIES) {
          failedRequests.push(request);
        } else {
          logger.error(`[offlineEdgeFunctions] Max retries (${MAX_EDGE_RETRIES}) reached for: ${request.functionName}, discarding`);
        }
      }
    }

    // Update queue with only failed requests
    safeSetStorage(QUEUE_KEY, JSON.stringify(failedRequests));

    // Dispatch update event safely
    safeDispatchEvent('offline-queue-updated');

    if (queue.length > failedRequests.length) {
      toast.success('Offline requests synced', {
        description: `${queue.length - failedRequests.length} request(s) processed successfully`,
      });
    }

    if (failedRequests.length > 0) {
      toast.error('Some requests failed', {
        description: `${failedRequests.length} request(s) could not be processed`,
      });
    }
  } catch (error) {
    logger.error('[offlineEdgeFunctions] processOfflineQueue error:', error);
  }
}

// NOTE: The `online` event listener is now handled by the unified syncOrchestrator
// in src/lib/syncOrchestrator.ts to prevent all sync mechanisms from firing simultaneously.
// processOfflineQueue() is registered as a sync task with priority 2.

// Export function to get queue count - always returns a number
export function getOfflineQueueCount(): number {
  try {
    const stored = safeGetStorage(QUEUE_KEY);
    if (!stored) return 0;
    
    const queue = JSON.parse(stored);
    return Array.isArray(queue) ? queue.length : 0;
  } catch {
    return 0;
  }
}